import { styled, css } from 'styled-components'

import {
  error,
  onSurface,
  onSurfaceDim,
  onSurfaceMuted,
  outline,
  outlineMuted,
  secondary,
  surfaceBright,
  textInputBoxShadow,
} from 'core/styles'

export type InputVariant = 'outline' | 'underline' | 'none' | 'compact' | 'compact-white-bg'

export const Fixture = styled.span`
  color: ${onSurfaceMuted};
`

const compact = css`
  border-width: 1px;
  border-radius: 8px;
  border-color: ${outline};
  padding: 0 12px;
  height: 32px;
  color: ${onSurface};
  font-size: 14px;
  font-weight: 500;

  &:hover {
    border-color: ${outline};
  }
`

export const common = css<{ $width?: string; $variant?: InputVariant; $disabled?: boolean; $errorMessage?: boolean }>`
  box-sizing: border-box;
  border-width: 0 0 1px;
  border-style: solid;
  border-color: transparent;
  width: ${(p) => (p.$width ? p.$width : 'auto')};

  ${(p) =>
    p.$disabled &&
    `
    // Matching the ReactSelect default styles (?) here for consistency
    color: #999999;
    background-color: #f2f2f2;
    border-color: #e6e6e6;
  `}

  ${(p) =>
    (p.$variant === 'underline' || p.$variant === 'outline') &&
    `
      border-color: ${outline};

      &:hover {
        border-color: ${outlineMuted};
      }
  `}

  ${(p) =>
    p.$errorMessage &&
    `
      border-color: ${error} !important;
    `}

  ${(p) =>
    p.$variant === 'outline' &&
    `
      padding: 8px;
      border-width: 1px;
      border-radius: 4px;
    `}

  ${(p) => p.$variant === 'compact' && compact}

  ${(p) =>
    p.$variant === 'compact-white-bg' &&
    css`
      ${compact}
      box-shadow: ${textInputBoxShadow};
      background-color: ${surfaceBright};
    `}


  &:focus-within:not([readonly]) {
    outline: none;
    border-color: ${secondary};
  }
`

export const commonInput = css`
  border: none;
  background-color: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 14px;

  ::placeholder {
    color: ${onSurfaceDim};
  }
`

const truncate = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const Wrapper = styled.div<{
  $width?: string
  $variant?: InputVariant
  $disabled?: boolean
  $errorMessage?: boolean
  $truncated?: boolean
}>`
  ${common}
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: 2px;
  align-items: center;

  input {
    ${commonInput}
    ${(p) => p.$truncated && truncate}
    &:focus-within {
      outline: none;
      border: none;
    }
  }
`

export const StyledInput = styled.input<{
  $width?: string
  $variant?: InputVariant
  $disabled?: boolean
  $errorMessage?: boolean
  $truncated?: boolean
}>`
  ${commonInput}
  ${common}
  ${(p) => p.$truncated && truncate}
`

export const ErrorMessage = styled.div`
  margin-top: 4px;
  color: ${error};
  font-size: 12px;
  font-weight: 400;
`
